import { cfetch } from '@confluence/network';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import {
	type SnippetData,
	SnippetError,
	type CreateSnippetBody,
	type FetchSnippetScope,
} from './blockServiceTypes.ts';
import { transformSnippet, transformSnippets } from './blockServiceUtils.ts';
import { type Snippet, SnippetBodyFormat, SnippetProduct } from '../snippetTypes.ts';
import { clearSnippetsCache, withSnippetsCache } from './snippetsCache.ts';

const COMMON_HEADERS = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
};

const BLOCK_SERVICE_API_URL = '/gateway/api/blocks/v1';
const SNIPPETS_PATH = '/snippets';

/**
 * Fetch snippets from the Block Service for Confluence. https://bitbucket.org/atlassian/block-service/src/main/
 * Please note that no error handling is done. Ensure you are handling bad response codes when using this.
 */
export const fetchSnippets = (): Promise<Snippet[]> => {
	return withSnippetsCache(async () => {
		const [userSnippetsResult, globalSnippetsResult] = await Promise.allSettled([
			fetchSnippetData('user'),
			fetchSnippetData('global'),
		]);

		const successfulSnippets: SnippetData[] = [];
		if (userSnippetsResult.status === 'fulfilled') {
			successfulSnippets.push(...userSnippetsResult.value);
		} else {
			logResultError(userSnippetsResult.reason, 'user');
		}

		if (globalSnippetsResult.status === 'fulfilled') {
			successfulSnippets.push(...globalSnippetsResult.value);
		} else {
			logResultError(globalSnippetsResult.reason, 'global');
		}

		return transformSnippets(successfulSnippets);
	});
};

const fetchSnippetData = async (scope: FetchSnippetScope): Promise<SnippetData[]> => {
	const params = new URLSearchParams({
		format: SnippetBodyFormat.ADF,
		product: SnippetProduct.CONFLUENCE,
		scope,
	});

	const response = await cfetch(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}?${params.toString()}`, {
		method: 'GET',
		headers: COMMON_HEADERS,
	});

	if (!response.ok) {
		throw new SnippetError(response.statusText);
	}

	const responseData = await response.json();
	if (!responseData.success) {
		return [];
	}

	// Success and error can both be present, so we need to log the error if it is present
	if (Array.isArray(responseData.error) && responseData.error.length > 0) {
		responseData.error.forEach((error: any) => {
			logResultError(error, scope);
		});
	}

	return responseData.success;
};

export const createSnippet = async (params: CreateSnippetBody): Promise<Snippet> => {
	const response = await cfetch(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}`, {
		method: 'POST',
		headers: COMMON_HEADERS,
		body: JSON.stringify(params),
	});

	if (!response.ok) {
		throw new SnippetError(response.statusText);
	}

	const responseData: SnippetData = await response.json();
	const snippet = await transformSnippet(responseData);

	// Clear the cache so the newly created snippet appears when fetching snippets again
	// (e.g., when navigating to a different page or when the extension provider rebuilds)
	// addSnippetQuickInsertItem only adds it to current editor instance's quick insert menu
	// navigating to a new edit page the extension providers rebuilds and call fetchSnippets which will return the cached data.
	clearSnippetsCache();

	if (snippet) {
		return snippet;
	} else {
		throw new Error('Unable to transform snippet data after creation');
	}
};

/**
 * Delete a snippet via the Block Service API.
 * Passes snippetId as the path segment after /snippets/.
 */
export const deleteSnippet = async (snippetId: string): Promise<void> => {
	const response = await cfetch(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}/${snippetId}`, {
		method: 'DELETE',
		headers: COMMON_HEADERS,
	});

	if (!response.ok) {
		throw new SnippetError(response.statusText);
	}
};

const logResultError = (reason: unknown, scope: FetchSnippetScope): void => {
	const reasonString = reason instanceof Error ? reason.message : JSON.stringify(reason);
	const errorToLog = new Error(`Error when fetching ${scope} scoped snippets: ${reasonString}`);
	getMonitoringClient().submitError(errorToLog, {
		attribution: Attribution.INTEGRATIONS,
	});
};
